/**
 * Promise wrappers around the shared http client for the SaaS billing API.
 *
 * Every rejection is an ``Error`` whose ``detail`` holds the backend's
 * human-readable message (``err.detail``), matching the FastAPI contract.
 * A failure callback is always passed to the client so it never falls back
 * to its own toast — callers decide how to surface errors.
 */
import { data_from_server_delete, data_from_server_get, data_from_server_post } from '@/shared/http/client';

const toError = (detail, fallback) => {
    const message = typeof detail === 'string' && detail.trim() ? detail : fallback;
    const err = new Error(message);
    err.detail = message;
    return err;
};

/**
 * ``data_from_server_get`` / ``data_from_server_delete`` hand the parsed body
 * to the success callback even on non-2xx (e.g. a 409 ``{detail}``), so a
 * response that lacks ``expectedKey`` but carries ``detail`` is an error.
 */
const looksLikeError = (payload, expectedKey) =>
    payload
    && typeof payload === 'object'
    && !(expectedKey in payload)
    && 'detail' in payload;

const getJson = (suffix, expectedKey, fallback) =>
    new Promise((resolve, reject) => {
        data_from_server_get(suffix, (payload) => {
            if (looksLikeError(payload, expectedKey)) {
                reject(toError(
                    typeof payload.detail === 'string' ? payload.detail : JSON.stringify(payload.detail),
                    fallback
                ));
                return;
            }
            resolve(payload);
        }).catch((e) => reject(toError(e?.detail || e?.message, fallback)));
    });

const postJson = (suffix, body, fallback) =>
    new Promise((resolve, reject) => {
        data_from_server_post(
            suffix,
            body,
            resolve,
            (detail) => reject(toError(detail, fallback))
        ).catch((e) => reject(toError(e?.detail || e?.message, fallback)));
    });

const deleteJson = (suffix, body, expectedKey, fallback) =>
    new Promise((resolve, reject) => {
        data_from_server_delete(
            suffix,
            body,
            (payload) => {
                if (looksLikeError(payload, expectedKey)) {
                    reject(toError(
                        typeof payload.detail === 'string' ? payload.detail : JSON.stringify(payload.detail),
                        fallback
                    ));
                    return;
                }
                resolve(payload);
            },
            (detail) => reject(toError(detail, fallback))
        ).catch((e) => reject(toError(e?.detail || e?.message, fallback)));
    });

const PROFILE_KEY = 'billing_enabled';

/** GET billing/profile → billing profile. */
export function fetchBillingProfile() {
    return getJson('billing/profile', PROFILE_KEY, 'Could not load billing details.');
}

/** GET billing/cancellation-reasons → ``{reasons: [{id, label}]}``. */
export function fetchCancellationReasons() {
    return getJson('billing/cancellation-reasons', 'reasons', 'Could not load cancellation reasons.');
}

/** POST billing/setup-intent → ``{client_secret, setup_intent_id}``. */
export function createSetupIntent(purpose) {
    return postJson('billing/setup-intent', { purpose }, 'Could not start the card setup.');
}

/** POST billing/payment-methods → profile (attaches a confirmed PaymentMethod). */
export function attachPaymentMethod({ payment_method_id, purpose, replace_payment_method_id }) {
    const body = { payment_method_id, purpose };
    if (replace_payment_method_id) {
        body.replace_payment_method_id = replace_payment_method_id;
    }
    return postJson('billing/payment-methods', body, 'Could not save the card.');
}

/** POST billing/payment-methods/{id}/primary → profile. */
export function setPrimaryPaymentMethod(paymentMethodId) {
    return postJson(
        `billing/payment-methods/${encodeURIComponent(paymentMethodId)}/primary`,
        {},
        'Could not update the primary card.'
    );
}

/** DELETE billing/payment-methods/{id} → profile (409 when primary). */
export function deletePaymentMethod(paymentMethodId) {
    return deleteJson(
        `billing/payment-methods/${encodeURIComponent(paymentMethodId)}`,
        {},
        PROFILE_KEY,
        'Could not delete the card.'
    );
}

/** Stripe Customer Portal flows accepted by the backend (``'manage'`` is its default). */
export const PORTAL_FLOWS = Object.freeze({ manage: 'manage', upgrade: 'upgrade' });

/** POST billing/portal-session ``{flow: 'manage'|'upgrade'}`` → ``{url}``. */
export function createPortalSession(flow = PORTAL_FLOWS.manage) {
    return postJson('billing/portal-session', { flow }, 'Could not open the billing portal.');
}

/** POST billing/coupon ``{code}`` → profile (404 when the code is invalid). */
export function redeemCoupon(code) {
    return postJson('billing/coupon', { code }, 'Could not redeem this coupon.');
}

/** DELETE billing/coupon → profile. */
export function removeCoupon() {
    return deleteJson('billing/coupon', {}, PROFILE_KEY, 'Could not remove the coupon.');
}

/** POST billing/cancel → profile (400 when neither reason nor comment). */
export function cancelPlan({ reason_id, comment }) {
    const body = {};
    if (reason_id) body.reason_id = reason_id;
    if (comment) body.comment = comment;
    return postJson('billing/cancel', body, 'Could not cancel the plan.');
}

/** POST billing/reactivate → profile. */
export function reactivatePlan() {
    return postJson('billing/reactivate', {}, 'Could not reactivate the plan.');
}
